import { Component, HostListener, inject, input, OnInit, output, signal, WritableSignal } from "@angular/core";
import { StorageService, StorageType } from "gast-common-ng2";
import { AnswerDto, TestResultDto } from 'gast-onset-ng2';
import { catchError, EMPTY } from 'rxjs';
import { ClozeTestModel } from '../model/cloze-test-model';
import { ClozeTestStyleModel } from '../model/cloze-test-style-model';
import { NgxSpinnerService } from "ngx-spinner";
import { ExerciseComponent } from "../exercise/exercise.component";
import { ExerciseExampleComponent } from "../exercise-example/exercise-example.component";
import { IntroductionComponent } from "../introduction/introduction.component";
import { IntroductionShortComponent } from "../introduction-short/introduction-short.component";
import { TestResultComponent } from "../test-result/test-result.component";
import { MobileKeyboardComponent } from "../mobile-keyboard/mobile-keyboard.component";
import { PlacementTestsService } from '../services/placement-tests.service';
import { CTestAntwortDto } from '../services/dto/c-test-antwort.dto';
import { CTestDto } from '../services/dto/c-test.dto';
import { IntroductionType } from "../model/introduction-type.enum";
import { InvalidTokenComponent } from "../invalid-token/invalid-token.component";

@Component({
  selector: "cloze-test",
  templateUrl: "./cloze-test.component.html",
  styleUrls: ["./cloze-test.component.scss"],
  standalone: true,
  imports: [ExerciseComponent, ExerciseExampleComponent, IntroductionComponent, IntroductionShortComponent, TestResultComponent, MobileKeyboardComponent, InvalidTokenComponent],
})
export class ClozeTestComponent implements OnInit {
  readonly MOBILE_INSTRUCTION_TITLE = "Hinweise zum Test";

  readonly #placementTestsService = inject(PlacementTestsService);
  readonly spinnerService = inject(NgxSpinnerService);
  readonly storageService = inject(StorageService);

  readonly mobileMode = input(false);
  readonly headerLogo = input('');
  readonly headerLogoTitle = input('');
  readonly headerLogoTitleImage = input('');
  readonly headerTitle = input('');
  readonly productId = input(3);
  readonly showResult = input(false);
  readonly clozeTestModel = input({} as ClozeTestModel);
  readonly clozeTestStyleModel = input({} as ClozeTestStyleModel);
  readonly results = input({} as TestResultDto);
  readonly showEndTestButton = input(true);
  readonly lang = input("de");
  readonly introductionType = input(IntroductionType.NO_INTRO);
  readonly showExerciseExample = input(false);
  readonly currentResults: WritableSignal<TestResultDto> = signal({} as TestResultDto);
  readonly IntroductionType = IntroductionType;
  startPageSubtitle = "";

  readonly testSaved = output<number>();
  readonly startTest = output<boolean>();
  readonly testResult = output<TestResultDto>();
  readonly clozeTest = output<boolean>();

  bookingId!: number;
  startPageTitle = "";
  startPageContent = "";
  exerciseTitle!: string;
  exerciseContent!: string;
  exerciseId!: number;
  currentExercise = 0;
  totalExercises: number | null = null;
  isLastExercise = false;
  cTestLogo!: string;
  cTestButtonColor!: string;

  isTestStarted = false;
  isTestExampleStarted = false;
  isTestOver = false;
  isLoadingTask = false;
  isInvalidToken = false;

  #booking: CTestDto | null = null;

  ngOnInit() {
    this.currentResults.set(this.results());
    this.bookingId = this.clozeTestModel().startWithBuchungId;

    // If the user refreshed the browser mid-test, notify the backend so it can
    // track that the session was interrupted.
    const bookingIdBrowserRefreshed = this.storageService.getItem("BrowserRefreshed", StorageType.Session);
    if (bookingIdBrowserRefreshed) {
      console.log("Browser refresh detected, updating count");
      this.#placementTestsService.updateBrowserRefreshCount(this.#serviceUrl, bookingIdBrowserRefreshed as number).subscribe();
      console.log("remove Browser refresh from storage");
      this.storageService.removeItem("BrowserRefreshed", StorageType.Session);
    }

    // NO_INTRO: mark test as started immediately so that when #loadNextTask()
    // resolves it renders the first exercise right away.
    // With an intro: isTestStarted stays false — #loadNextTask() still runs in
    // the background to pre-fetch the first task and to detect if the test is
    // already completed (in which case #loadResults() is called and the result
    // page is shown before the intro ever becomes visible).
    if (this.introductionType() === IntroductionType.NO_INTRO) {
      this.isTestStarted = true;
    } else {
      if (this.mobileMode()) {
        this.startPageTitle = this.MOBILE_INSTRUCTION_TITLE;
      }
    }
    this.#loadNextTask();

    this.#findLogo();
  }

  @HostListener("window:beforeunload")
  unloadHandler() {
    if (this.bookingId) {
      this.storageService.setItem("BrowserRefreshed", this.bookingId, StorageType.Session);
    }
  }

  // Called by cloze-test-exercise when the user (or the task timer) advances to
  // the next task. Saves answers, then either moves to the next locally-cached
  // task or fetches a new batch from the backend.
  onNextExercise(answers: AnswerDto[]) {
    const antworten = this.#mapAnswers(answers);
    this.#placementTestsService.saveTaskAnswers(this.#serviceUrl, this.bookingId, antworten).subscribe(() => {
      this.testSaved.emit(this.bookingId);
      if (this.isLastExercise) {
        // Backend flagged this task as the last one → go straight to results.
        this.#loadResults();
      } else {
        // Check whether the already-fetched booking still has tasks ahead of
        // the current position before making another network request.
        const hasMoreLocally = this.#booking?.aufgaben.some(a => a.position > this.currentExercise);
        if (hasMoreLocally) {
          this.#prepareNextExercise();
        } else {
          this.#loadNextTask();
        }
      }
    });
  }

  // Called by cloze-test-exercise when the user (or the task timer) ends the
  // last task. Saves answers and transitions to the result page.
  onTestEnd(answers: AnswerDto[]): void {
    this.spinnerService.show();
    const antworten = this.#mapAnswers(answers);
    this.#placementTestsService.saveTaskAnswers(this.#serviceUrl, this.bookingId, antworten).subscribe(() => {
      this.#loadResults();
    });
  }

  // Called when the user clicks "Start" on the intro page.
  // If #loadNextTask() already completed in the background the booking is ready
  // and we can jump straight to the first exercise. Otherwise trigger a fresh load.
  onStartTest(start: boolean) {
    this.isTestStarted = true;
    if (this.#booking) {
      this.#prepareNextExercise();
    } else {
      this.#loadNextTask();
    }
    this.startTest.emit(start);
  }

  onStartTestExample() {
    this.isTestExampleStarted = true;
  }

  onClozeTest(cloze: boolean): void {
    this.clozeTest.emit(cloze);
  }

  // Fetches the next batch of tasks from the backend.
  // Always called on init (regardless of intro type) so that:
  //   - If the test is already completed, the backend returns an error and
  //     #loadResults() is called immediately — the result page is shown before
  //     the intro is ever rendered.
  //   - If tasks exist and isTestStarted is true (NO_INTRO), the first exercise
  //     is rendered as soon as the response arrives.
  //   - If tasks exist and isTestStarted is false (with intro), the booking is
  //     cached silently; the intro is displayed and the first exercise is ready
  //     the moment the user clicks "Start".
  #loadNextTask(): void {
    if (!this.bookingId) {
      // No booking ID means there is nothing to load — go straight to results.
      this.isTestOver = true;
      return;
    }
    this.isLoadingTask = true;
    this.spinnerService.show();
    this.#placementTestsService.getNextTask(this.#serviceUrl, this.bookingId).pipe(
      catchError((err) => {
        this.isLoadingTask = false;
        this.spinnerService.hide();
        if (err.status === 401 || err.status === 403) {
          // Token is invalid or expired — show the invalid-token screen.
          this.isInvalidToken = true;
        } else {
          // Any other error (e.g. no more tasks / test already done) →
          // fetch and display the final score.
          this.#loadResults();
        }
        return EMPTY;
      })
    ).subscribe(cTest => {
      this.#booking = cTest;
      this.bookingId = cTest.buchungId || this.bookingId;
      this.totalExercises = cTest.numAufgaben;
      this.isLoadingTask = false;
      this.spinnerService.hide();
      // Only render the exercise immediately if the test has been started
      // (either NO_INTRO or the user has already clicked "Start").
      if (this.isTestStarted) {
        this.#prepareNextExercise();
      }
    });
  }

  // Picks the next task from the already-loaded booking and updates the
  // exercise inputs. If no further task exists in the local cache, fetches
  // the final score instead (test is over).
  #prepareNextExercise(): void {
    if (!this.#booking) return;
    const next = this.#booking.aufgaben.find(a => a.position > this.currentExercise);
    if (!next) {
      this.#loadResults();
      return;
    }
    this.exerciseId = next.id;
    this.exerciseTitle = next.title;
    this.exerciseContent = next.content;
    this.currentExercise = next.position;
    this.isLastExercise = next.last;
  }

  // Fetches the final score from the backend and switches the view to the
  // result page. Also records when the result was shown.
  #loadResults(): void {
    this.spinnerService.show();
    this.#placementTestsService.getScore(this.#serviceUrl, this.bookingId).subscribe(scoreDto => {
      const result = {
        score: scoreDto.score,
        graduation: scoreDto.graduation,
        maxScore: scoreDto.maxScore,
      } as TestResultDto;
      this.isTestOver = true;
      this.currentResults.set(result);
      this.testResult.emit(result);
      this.clozeTest.emit(true);
      this.spinnerService.hide();
      this.#placementTestsService.updateResultShownTime(this.#serviceUrl, this.bookingId).subscribe();
      console.log(`update result shown time booking id: ${this.bookingId}`);
    });
  }

  #mapAnswers(answers: AnswerDto[]): CTestAntwortDto[] {
    return answers.map(a => ({
      buchungId: a.buchungId ?? this.bookingId,
      aufgabeId: a.aufgabeId ?? 0,
      nr: a.nr ?? 0,
      antwort: a.antwort,
    }));
  }

  get #serviceUrl(): string {
    return this.clozeTestModel().onsetServiceUrl;
  }

  #findLogo() {
    this.cTestLogo = this.clozeTestStyleModel().logo;
    this.cTestButtonColor = this.clozeTestStyleModel().buttonsColor;
  }
}
